import * as React from 'react';
import { ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import {
  AdminDataWorkOpenAccessFundedDTO,
  AdminDataWorkOrcidSyncDTO,
  ApiResponse,
  EventDTO,
  EventPlaceDTO,
  ExternalIdDTO,
  ReferencedOrganisationDTO,
  ReferencedProjectDTO,
  UrlDTO,
  WorkApi,
  WorkDescriptionDTO,
  WorkDTO,
  WorkGeoLocationDTO,
  WorkGeoLocationPointDTO,
  WorkHasPartDTO,
  WorkIsPartOfDTO,
  WorkIsPartOfOtherDTO,
  WorkKeywordDTO,
  WorkOrganisationDTO,
  WorkOrganisationOtherDTO,
  WorkOtherTitleDTO,
  WorkPersonDTO,
  WorkPersonOtherDTO,
  WorkPlaceDTO,
  WorkProjectDTO,
  WorkProjectOtherDTO,
  WorkPublisherDTO,
  WorkPublisherPlaceDTO,
  WorkRelatedIdentifierDTO,
  WorkRelatedIdentifierInternalDTO,
  WorkTitleAbbreviationDTO,
} from '../../api/core';
import { NavigateFunction, Params, useNavigate, useParams } from 'react-router';
import { AuthContext } from '../../Auth/components/AuthProvider';
import { getAuthMiddleware } from '../../Auth/utils/GetAuthMiddleware';
import { Loading } from '../system/Loading';
import { Tag } from 'primereact/tag';
import {
  boolFilter,
  formSubjectFilter,
  peerReviewStatusFilter,
  pubStatusFilter,
  pubtypeFilter,
  resourceTypeFilter,
} from '../../Vocabulary/data';
import { ParsedMathJax } from '../components/ParsedMathJax';
import { getMandantSettingsByTopOrga } from '../../util/mandant-settings-utils';
import { IntlShape, useIntl } from 'react-intl';
import { ExternalLink } from '../components/ExternalLink';
import { Button } from 'primereact/button';
import { getNotation } from '../../Vocabulary/util';
import { Row } from './components/layout/Row';
import moment from 'moment';
import { renderDetailAccordionTab } from './components/layout/renderDetailAccordionTab';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { deleteWork, requestDeleteWork } from '../../util/actions/work-actions';
import View from './components/layout/View';
import { CitationDialog } from '../components/CitationDialog';
import { DuplicateDialog } from '../components/DuplicateDialog';
import { BreakingList } from './components/layout/BreakingList';
import { MandantSettings } from '../../util/types';
import { InternalLink } from '../components/InternalLink';
import { ExternalId } from './components/ExternalId';
import { CustomBadge } from '../components/CustomBadge';
import { SubRow } from './components/layout/Subrow';
import { localDate, localDateTime } from '../../util/intl.utils';
import { QuoteIcon } from '../components/QuoteIcon';
import { headerExcludedRoles } from '../../Settings/config';
import { Stamp } from '../components/Stamp';
import { UrlRender, UrlsRow } from './components/UrlsRow';
import { SubjectsRow } from './components/SubjectsRow';
import { LinkedPerson } from './components/LinkedPerson';
import { LinkedPersonOther } from './components/LinkedPersonOther';

export const DetailWork = (): ReactNode => {
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();
  const navigate: NavigateFunction = useNavigate();
  const params: Readonly<Params<string>> = useParams();
  const {actions, authorizer} = useContext(AuthContext);
  const workApi: WorkApi = new WorkApi().withMiddleware(getAuthMiddleware(actions));
  const [work, setWork] = useState<WorkDTO>();
  const [citation, setCitation] = useState<boolean>(false);
  const [duplicate, setDuplicate] = useState<boolean>(false);

  useEffect(() => {
    if (params.id === undefined) {
      return void navigate('/search/works');
    }
    const id: number = Number(params.id);
    if (isNaN(id)) {
      toast.notify({ severity: 'error', summary: intl.formatMessage({ id: 'message.loading.failed.message' }) });
      return void navigate('/error/404');
    }
    if (!work || id !== work.id){
      window.scrollTo(0, 0);
      void fetchData(id);
    }
  }, [params.id]);

  const fetchData = async (workId: number): Promise<void> => {
    try {
      const response: ApiResponse<WorkDTO> = await workApi.getWorkRaw({workId});
      if (!response.raw.ok) {
        toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
        return void navigate(`/error/${response?.raw?.status || 500}`);
      }
      setWork(await response.value());
    } catch (error) {
      toast.notify({severity: 'error', summary: intl.formatMessage({id: 'message.loading.failed.message'})});
      return void navigate(`/error/${error?.status || 500}`);
    }
  };

  if (!work) {
    return <Loading />;
  }

  // RENDER DATA FOR HEADER

  const renderTags = (workDTO: WorkDTO): ReactNode => {
    return <>
      <Tag severity={'secondary'} value={pubtypeFilter(workDTO.pubtype)}/>
      {workDTO.formSubject && <Tag severity={'success'} value={formSubjectFilter(workDTO.formSubject)}/>}
      {workDTO.resourceType && <Tag severity={'warning'} value={resourceTypeFilter(workDTO.resourceType)}/>}
      {workDTO.publicationStatus && <Tag severity={'danger'} value={pubStatusFilter(workDTO.publicationStatus)}/>}
    </>;
  };

  const renderTitle = (workDTO: WorkDTO): ReactNode => {
    return [workDTO.title, workDTO.subseriesTitle]
      .filter((i: string): boolean => !!i && i.length > 0)
      .map<React.ReactNode>((title: string, index: number): ReactNode => <ParsedMathJax key={index} content={title} />)
      .reduce((prev: ReactNode, curr: ReactNode): ReactNode => [prev, ' / ', curr]);
  };

  const renderSubtitle = (workDTO: WorkDTO): ReactNode => {
    return <h2 className={'my-0 text-lg'}><ParsedMathJax content={workDTO.subtitle} /></h2>;
  };

  const renderPersons = (workDTO: WorkDTO): ReactNode => {
    const persons: { sequence: number; element: ReactNode }[] = (workDTO.workPersons || [])
      .filter((wp: WorkPersonDTO): boolean => !headerExcludedRoles.includes(wp.role))
      .map((wp: WorkPersonDTO): {
        sequence: number;
        element: ReactNode;
      } => {
        const topOrgaLogos: ReactNode[] = [...new Set(wp.topOrgas || [])]
          .map((topOrga: string): MandantSettings => getMandantSettingsByTopOrga(topOrga))
          .filter((ms: MandantSettings): boolean => !!ms && !!ms.icon)
          .map((ms: MandantSettings, idx: number): ReactNode => <img key={idx} src={ms.icon}
                                                                     style={{height: '1.5rem', width: '1.5rem'}}
                                                                     alt={`${ms.mandant} Logo `}/>)
        ;
        return {
          sequence: wp.sequence,
          element:
            <div key={wp.sequence}>
              <CustomBadge
                idx={wp.sequence}
                label={[wp.personName.givenname, wp.personName.surname].filter(Boolean).join(' ')}
                internalLink={`/person/${wp.personName.personId}`}
                images={topOrgaLogos}/>
            </div>,
        };
      });

    const personOthers: {
      sequence: number;
      element: ReactNode;
    }[] = (workDTO.workPersonOthers || [])
      .filter((wp: WorkPersonOtherDTO): boolean => !headerExcludedRoles.includes(wp.role))
      .map((wp: WorkPersonOtherDTO): { sequence: number; element: ReactNode } => {
        return {
          sequence: wp.sequence,
          element:
            <div key={wp.sequence}>
              <CustomBadge idx={wp.sequence} label={[wp.givenname, wp.surname].filter(Boolean).join(' ')}/>
            </div>,
        };
      });
    return <BreakingList separator={''} limits={10} initialShow={true}>
      {[...persons, ...personOthers]
        .sort((a, b) => a.sequence > b.sequence ? 1 : -1)
        .map((item): ReactNode => item.element)
      }
    </BreakingList>;
  };

  const renderIssued = (workDTO: WorkDTO): ReactNode => {
    if (!workDTO.issued) return undefined;
    return <div><b
      className="head-year">{Math.floor(workDTO.issued / Math.pow(10, workDTO.issued.toString().length - 4))}</b>
    </div>;
  };

  const renderIsPartOfs = (workDTO: WorkDTO): ReactNode => {
    if (!(workDTO.workIsPartOfs || []).length) return undefined;
    return <div className={'flex'}>
      {workDTO.workIsPartOfs.map((ipo: WorkIsPartOfDTO, index: number) =>
        <div key={index} className={'flex'}>
          <BreakingList separator={', '}>
            <strong>
              <InternalLink to={`/work/${ipo.referencedWork.id}`}>
                <div className={'inline-flex'}><ParsedMathJax content={ipo.referencedWork.title} /></div>
                {ipo.referencedWork.subseriesTitle &&
                  <div className={'inline-flex'}> /&nbsp;{<ParsedMathJax content={ipo.referencedWork.subseriesTitle} />}</div>}
              </InternalLink>
            </strong>
            {ipo.volume && `${intl.formatMessage({id: 'work.attr.part.attr.volume.label'})}: ${ipo.volume}`}
            {ipo.issue && `${intl.formatMessage({id: 'work.attr.part.attr.issue.label'})}: ${ipo.issue}`}
            {ipo.articleId && `${intl.formatMessage({id: 'work.attr.part.attr.articleId.label'})}: ${ipo.articleId}`}
            {ipo.pageFirst && ipo.pageLast && `${intl.formatMessage({id: 'work.attr.part.attr.pages.label'})}: ${ipo.pageFirst}-${ipo.pageLast}`}
            {ipo.pageFirst && !ipo.pageLast && `${intl.formatMessage({id: 'work.attr.part.attr.pageFirst.label'})}: ${ipo.pageFirst}`}
            {!ipo.pageFirst && ipo.pageLast && `${intl.formatMessage({id: 'work.attr.part.attr.pageLast.label'})}: ${ipo.pageLast}`}
          </BreakingList>
        </div>,
      )}
    </div>;
  };

  const renderIssuedIsPartOf = (workDTO: WorkDTO): ReactNode => {
    const issued = renderIssued(workDTO);
    const isPartOf = renderIsPartOfs(workDTO);
    return (
      <div className={'flex gap-1'}>
        {issued}
        {issued && isPartOf && ' in '}
        {isPartOf}
      </div>
    );
  };

  const renderEdition = (workDTO: WorkDTO): ReactNode => {
    return <>
      {
        [
          workDTO.workPublishers ? `${intl.formatMessage({id: 'work.attr.publishers.label'})}: ${(workDTO.workPublishers || []).map((p: WorkPublisherDTO): string => p.publisher.name).join(', ')}` : null,
          workDTO.edition ? `${workDTO.edition}. ${intl.formatMessage({id: 'work.attr.edition.label'})}` : null,
        ]
          .filter((Boolean))
          .join(' - ')
      }
    </>;
  };

  const renderDataVersion = (workDTO: WorkDTO): ReactNode => {
    return <div>{intl.formatMessage({id: 'work.attr.dataVersion.label'})}: {workDTO.version}</div>;
  };

  const renderWorkDescription = (workDTO: WorkDTO): ReactNode => {
    return <div className={'flex flex-column gap-1'}>
      {workDTO.workDescriptions
        .filter((description: WorkDescriptionDTO): boolean => !!description.content)
        .map((description: WorkDescriptionDTO, index: number) => <p key={index}>
          <ParsedMathJax content={description.content} /></p>)
      }
    </div>;
  };

  const renderKeywords = (workDTO: WorkDTO): ReactNode => {
    if (!workDTO.workKeywords || !workDTO.workKeywords.length) return <></>;
    return (
      <BreakingList>
        {(workDTO.workKeywords || [])
          .filter((keyword: WorkKeywordDTO): boolean => !!keyword.keyword)
          .map((keyword: WorkKeywordDTO, index: number) => (
            <div key={index}>{keyword.keyword}</div>
          ))}
      </BreakingList>
    );
  };

  const renderUrls = (workDTO: WorkDTO): ReactNode => {
    if (!workDTO.urls || !workDTO.urls.length) return <></>;
    const fulltextUrls: UrlDTO[] = (workDTO.urls || []).filter((e: UrlDTO): boolean => e.type === 'fulltext');
    if (!fulltextUrls.length) return <></>;
    return <BreakingList>
      {fulltextUrls
        .map((url: UrlDTO, index: number, array: UrlDTO[]) => <div key={index}>
          <ExternalLink href={url.url}>
            {intl.formatMessage({id: 'work.link.viewFullText'})}
            {array.length > 1 && ` ${index + 1}`}
          </ExternalLink>
        </div>)
      }
    </BreakingList>;
  };

  // RENDER DATA FOR DETAILS

  const renderTitleData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.title) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.title.label'})}><ParsedMathJax content={workDTO.title} /></Row>);
    }
    if (workDTO.subtitle) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.subtitle.label'})}><ParsedMathJax content={workDTO.subtitle} /></Row>);
    }
    if (workDTO.titleSupplement) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.titleSupplement.label'})}><ParsedMathJax content={workDTO.titleSupplement} /></Row>);
    }
    if (workDTO.workOtherTitles && workDTO.workOtherTitles.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.otherTitles.label'})}>
        {workDTO.workOtherTitles.map((workOtherTitle: WorkOtherTitleDTO, index: number): ReactNode =>
          <div key={index}>
            <ParsedMathJax content={workOtherTitle.otherTitle} />
            {getNotation('language_list', workOtherTitle.language, intl.locale)}
          </div>,
        )}
      </Row>);
    }
    if (workDTO.subseriesTitle) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.subseriesTitle.label'})}>
        <ParsedMathJax content={workDTO.subseriesTitle} />
      </Row>);
    }
    if (workDTO.workTitleAbbreviations && workDTO.workTitleAbbreviations.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.titleAbbreviations.label'})}>
        {workDTO.workTitleAbbreviations.map((abbr: WorkTitleAbbreviationDTO, index: number): ReactNode =>
          <ParsedMathJax key={index} content={abbr.abbreviation} />,
        )}
      </Row>);
    }
    if (workDTO.workPersons && workDTO.workPersons.length) {
      rows.push(<LinkedPerson linkedPersons={workDTO.workPersons} />);
    }
    if (workDTO.workPersonOthers && workDTO.workPersonOthers.length) {
      rows.push(<LinkedPersonOther linkedPersons={workDTO.workPersonOthers} />);
    }
    if (workDTO.workOrganisations && workDTO.workOrganisations.length > 0) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.workOrganisations.label'})}>
        {workDTO.workOrganisations.map((workOrganisation: WorkOrganisationDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column gap-1'}>
            <div className={'flex flex-row justify-content-between'}>
              <div className={'flex gap-1 flex-wrap'}><InternalLink
                to={`/organisation/${workOrganisation.organisation.id}`}><strong>
                <div className={'inline-block'}>{workOrganisation.organisation.name}</div>
              </strong></InternalLink><span>({getNotation('corporation_role_list', workOrganisation.role, intl.locale)})</span>
              </div>
              <div><small className={'white-space-nowrap'}>{`ID: ${workOrganisation.organisation.id}`}</small></div>
            </div>
          </div>)}
      </Row>);
    }
    if (workDTO.workOrganisationOthers && workDTO.workOrganisationOthers.length > 0) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.workOrganisationOthers.label'})}>
        {workDTO.workOrganisationOthers.map((workOrganisation: WorkOrganisationOtherDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column gap-1'}>
            <div className={'flex flex-row justify-content-between'}>
              <div className={'flex gap-1 flex-wrap'}><strong>
                <div className={'inline-block'}>{workOrganisation.name}</div>
              </strong><span>({getNotation('corporation_role_list', workOrganisation.role, intl.locale)})</span></div>
            </div>
          </div>)}
      </Row>);
    }
    if (workDTO.workProjects && workDTO.workProjects.length > 0) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.workProjects.label'})}>
        {workDTO.workProjects.map((workProject: WorkProjectDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column gap-1'}>
            <div className={'flex flex-row justify-content-between'}>
              <div className={'flex gap-1 flex-wrap'}><InternalLink to={`/project/${workProject.project.id}`}><strong>
                <div className={'inline-block'}>{workProject.project.name}</div>
              </strong></InternalLink><span>({getNotation('corporation_role_list', workProject.role, intl.locale)})</span>
              </div>
              <div><small className={'white-space-nowrap'}>{`ID: ${workProject.project.id}`}</small></div>
            </div>
          </div>)}
      </Row>);
    }
    if (workDTO.workProjectOthers && workDTO.workProjectOthers.length > 0) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.workProjectOthers.label'})}>
        {workDTO.workProjectOthers.map((workProject: WorkProjectOtherDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column gap-1'}>
            <div className={'flex flex-row justify-content-between'}>
              <div className={'flex gap-1 flex-wrap'}><strong>
                <div className={'inline-block'}>{workProject.name}</div>
              </strong><span>({getNotation('corporation_role_list', workProject.role, intl.locale)})</span></div>
            </div>
          </div>)}
      </Row>);
    }
    if (workDTO.events && workDTO.events.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.events.label' })}>
          {(workDTO.events || []).map((event: EventDTO, index: number): ReactNode => {
            return (
              <div key={index} className={'flex flex-column'}>
                <div className={'flex flex-row gap-1'}>
                  {event.eventNumbering && <span>{event.eventNumbering}. </span>}
                  <span className={'font-bold'}>{event.eventName}</span>
                </div>
                {event.eventPlaces && event.eventPlaces.length > 0 && (
                  <span>
                    in {event.eventPlaces.map((ep: EventPlaceDTO): string => ep.eventPlace).filter(Boolean).join(', ')}
                  </span>
                )}
                {(event.startDate || event.endDate) && (
                  <span>
                    {localDate(event.startDate, intl.locale)}
                    {event.endDate && event.endDate !== event.startDate && (
                      <span> - {localDate(event.endDate, intl.locale)}</span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </Row>
      );
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.titleData.title');
  };

  const renderBasicData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.pubtype) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.pubtype.label'})}>{pubtypeFilter(workDTO.pubtype)}</Row>);
    }
    if (workDTO.formSubject) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.formSubject.label'})}>{formSubjectFilter(workDTO.formSubject)}</Row>);
    }
    if (workDTO.publicationStatus) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.publicationStatus.label'})}>{pubStatusFilter(workDTO.publicationStatus)}</Row>);
    }
    if (workDTO.version) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.version.label'})}>{getNotation('version_list', workDTO.version, intl.locale)}</Row>);
    }
    if (workDTO.resourceType) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.resourceType.label'})}>{getNotation('resource_type_list', workDTO.resourceType, intl.locale)}</Row>);
    }
    if (workDTO.workLanguages && workDTO.workLanguages.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.languages.label'})}>
        <div className={'flex gap-1'}>
          {workDTO.workLanguages.map((language, index, array) =>
            <div key={index}>
              {getNotation('language_list', language.language, intl.locale)}
              {index + 1 < array.length && ', '}
            </div>,
          )}
        </div>
      </Row>);
    }
    if (workDTO.edition) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.edition.label'})}>{workDTO.edition}</Row>);
    }
    if (workDTO.dataVersion) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.dataVersion.label'})}>{workDTO.dataVersion}</Row>);
    }
    if (workDTO.numberOfVolumes) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.numberOfVolumes.label'})}>{workDTO.numberOfVolumes}</Row>);
    }
    if (workDTO.issued) {
      const issuedString: string = workDTO.issued.toString();
      switch (true) {
        case issuedString.length <= 4:
          rows.push(<Row title={intl.formatMessage({id: 'work.attr.issued.label'})}>{issuedString}</Row>);
          break;
        case issuedString.length <= 6:
          rows.push(<Row
            title={intl.formatMessage({id: 'work.attr.issued.label'})}>{issuedString.substring(0, 4) + '-' + issuedString.substring(4, issuedString.length)}</Row>);
          break;
        case issuedString.length <= 8:
          rows.push(<Row
            title={intl.formatMessage({id: 'work.attr.issued.label'})}>{issuedString.substring(0, 4) + '-' + issuedString.substring(4, 6) + '-' + issuedString.substring(6, issuedString.length)}</Row>);
          break;
        default:
          rows.push(<Row title={intl.formatMessage({id: 'work.attr.issued.label'})}>{issuedString}</Row>);
          break;
      }
    }
    if (workDTO.workDateTypes && workDTO.workDateTypes.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.dates.label'})}>
        {workDTO.workDateTypes.map((workDateType, index) =>
          <SubRow key={index}>
            {getNotation('date_types_list', workDateType.type, intl.locale)}
            {moment(workDateType.date).format('YYYY/MM/DD')}
          </SubRow>,
        )}
      </Row>);
    }
    if (workDTO.workPlaces && workDTO.workPlaces.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.places.label'})}>
        {workDTO.workPlaces.map((place: WorkPlaceDTO, index: number) => <div key={index}>{place.place}</div>)}
      </Row>);
    }
    if (workDTO.workPublishers && workDTO.workPublishers.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.publishers.label' })}>
          {(workDTO.workPublishers || []).map((publisher: WorkPublisherDTO, index: number) => (
            <div key={index} className={'flex justify-content-between'}>
              <div>
                {publisher.publisher.name}
                {publisher.workPublisherPlace &&
                  publisher.workPublisherPlace.length > 0 &&
                  ` (${publisher.workPublisherPlace
                    .filter(Boolean)
                    .map((place: WorkPublisherPlaceDTO): string => place.place)
                    .join(', ')})`}
              </div>
              <div>
                <small className={'white-space-nowrap'}>{`ID: ${publisher.id}`}</small>
              </div>
            </div>
          ))}
        </Row>
      );
    }
    if (workDTO.numberOfPages) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.numberOfPages.label'})}>{workDTO.numberOfPages}</Row>);
    }
    if (workDTO.size) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.size.label'})}>{workDTO.size}</Row>);
    }
    if (workDTO.number) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.number.label'})}>{workDTO.number}</Row>);
    }
    if (workDTO.urls && workDTO.urls.length) {
      rows.push(
        <UrlsRow
          title={intl.formatMessage({ id: 'work.attr.urls.label' })}
          urls={(workDTO.urls || []).map(
            (url: UrlDTO): UrlRender => ({ url: url.url, type: getNotation('url_list_work', url.type, intl.locale) })
          )}
        />
      );}
    if (workDTO.format) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.format.label'})}>{getNotation('format_list', workDTO.format, intl.locale)}</Row>);
    }
    if (workDTO.medium) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.medium.label'})}>{getNotation('medium_list', workDTO.medium, intl.locale)}</Row>);
    }
    if (workDTO.peerReviewed) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.peerReviewed.label'})}>{peerReviewStatusFilter(workDTO.peerReviewed)}</Row>);
    }
    if (workDTO.externalIds && workDTO.externalIds.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.externalIds.label' })}>
          {(workDTO.externalIds || []).map(
            (externalId: ExternalIdDTO, index: number): ReactNode => (
              <div key={index}>
                <ExternalId id={externalId} entity={'work'} />
              </div>
            )
          )}
        </Row>
      );
    }
    if (workDTO.note) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.note.label'})}>
        <p>{workDTO.note}</p>
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.basicData.title');
  };

  const renderSpecificData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.publicationNumber) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.publicationNumber.label'})}>{workDTO.publicationNumber}</Row>);
    }
    if (workDTO.applicationNumber) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.applicationNumber.label'})}>{workDTO.applicationNumber}</Row>);
    }
    if (workDTO.applicationCountry) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.applicationCountry.label'})}>{getNotation('country_list', workDTO.applicationCountry, intl.locale)}</Row>);
    }
    if (workDTO.priorityPatentApplication !== undefined) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.priorityPatentApplication.label'})}>{boolFilter(workDTO.priorityPatentApplication)}</Row>);
    }
    if (workDTO.priorityCountry) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.priorityCountry.label'})}>{getNotation('country_list', workDTO.priorityCountry, intl.locale)}</Row>);
    }
    if (workDTO.priorityNumber) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.priorityNumber.label'})}>{workDTO.priorityNumber}</Row>);
    }
    if (workDTO.grantNumber) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.grantNumber.label'})}>{workDTO.grantNumber}</Row>);
    }
    if (workDTO.patentFamily) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.patentFamily.label'})}>{workDTO.patentFamily}</Row>);
    }
    if (workDTO.patentId) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.patentId.label'})}>{workDTO.patentId}</Row>);
    }
    if (workDTO.patentType) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.patentType.label'})}>{getNotation('affiliations_list', workDTO.patentType, intl.locale)}</Row>);
    }
    if (workDTO.workGeoLocations && workDTO.workGeoLocations.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.geoLocation.label'})}>
        {workDTO.workGeoLocations.map((location: WorkGeoLocationDTO, index: number): ReactNode => {
          return <div key={index} className={'flex flex-column'}>
            <strong>{index + 1}. {intl.formatMessage({id: 'work.attr.geoLocation.label'})}</strong>
            {(location.pointLatitude || location.pointLongitude) && <div>
              {intl.formatMessage({id: 'work.attr.geoLocation.attr.latitude.label'})}: {location.pointLatitude ?? '-'},
              {intl.formatMessage({id: 'work.attr.geoLocation.attr.longitude.label'})}: {location.pointLongitude ?? '-'}
            </div>}
            {(location.boxEastBoundLongitude || location.boxWestBoundLongitude || location.boxNorthBoundLatitude || location.boxSouthBoundLatitude) &&
              <div className={'flex flex-column'}>
                <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.geoLocationBox.label'})}</div>
                <div className={'ml-2 flex flex-column'}>
                  <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.boxNorthBoundLatitude.label'})}: {location.boxNorthBoundLatitude || '-'}</div>
                  <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.boxSouthBoundLatitude.label'})}: {location.boxSouthBoundLatitude || '-'}</div>
                  <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.boxEastBoundLongitude.label'})}: {location.boxEastBoundLongitude || '-'}</div>
                  <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.boxWestBoundLongitude.label'})}: {location.boxWestBoundLongitude || '-'}</div>
                </div>
              </div>
            }
            {location.place && <div>
              {intl.formatMessage({id: 'work.attr.geoLocation.attr.place.label'})}: {location.place}
            </div>}
            {location.workGeoLocationPolygon && <div className={'flex flex-column'}>
              <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.geoLocationPolygon.label'})}</div>
              <div className={'ml-2 flex flex-column'}>
                {(location.workGeoLocationPolygon.inLatitude || location.workGeoLocationPolygon.inLongitude) && <div>
                  {intl.formatMessage({id: 'work.attr.geoLocation.attr.latitude.label'})}: {location.workGeoLocationPolygon.inLatitude ?? '-'},
                  {intl.formatMessage({id: 'work.attr.geoLocation.attr.longitude.label'})}: {location.workGeoLocationPolygon.inLongitude ?? '-'}
                </div>}
                {location.workGeoLocationPolygon.workGeoLocationPoints && location.workGeoLocationPolygon.workGeoLocationPoints.length > 0 &&
                  <div className={'flex flex-column'}>
                    <div>{intl.formatMessage({id: 'work.attr.geoLocation.attr.geoLocationPolygon.attr.geoLocationPolygonPoint.label'})}</div>
                    <div className={'ml-2 flex flex-column'}>
                      {location.workGeoLocationPolygon.workGeoLocationPoints.map((point: WorkGeoLocationPointDTO, idx: number): ReactNode =>
                        <div key={idx} className={'ml-2'}>
                          {intl.formatMessage({id: 'work.attr.geoLocation.attr.latitude.label'})}: {point.latitude ?? '-'},
                          {intl.formatMessage({id: 'work.attr.geoLocation.attr.longitude.label'})}: {point.longitude ?? '-'}
                        </div>,
                      )}
                    </div>
                  </div>
                }
              </div>

            </div>}
          </div>;
        })}
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.specificInfo.title');
  };

  const renderIsPartOfData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.workIsPartOfs && workDTO.workIsPartOfs.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.isPartOf.label'})}>
        {workDTO.workIsPartOfs
          .sort((a: WorkIsPartOfDTO, b: WorkIsPartOfDTO): number =>
            ((parseInt(a.volume, 10) || 0) - (parseInt(b.volume, 10) || 0))
            || (parseInt(a.issue, 10) || 0) - (parseInt(b.issue, 10) || 0)
            || (parseInt(a.articleId, 10) || 0) - (parseInt(b.articleId, 10) || 0)
            || (parseInt(a.pageFirst, 10) || 0) - (parseInt(b.pageFirst, 10) || 0))
          .map((isPartOf: WorkIsPartOfDTO, index: number): ReactNode => {
            return <div key={index} className={'flex flex-column gap-1'}>
              <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.referencedWork.label'})}</div>
                <div className={'flex flex-row justify-content-between'}>
                  <div><InternalLink to={`/work/${isPartOf.referencedWork.id}`}><strong>
                    <div className={'inline-block'}><ParsedMathJax content={isPartOf.referencedWork.title} /></div>
                  </strong></InternalLink></div>
                  <div><small className={'white-space-nowrap'}>{`ID: ${isPartOf.referencedWork.id}`}</small></div>
                </div>
              </SubRow>
              {(isPartOf.pageFirst || isPartOf.pageLast) && <SubRow>
                <div>
                  {isPartOf.pageFirst && isPartOf.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pages.label'})}
                  {isPartOf.pageFirst && !isPartOf.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageFirst.label'})}
                  {!isPartOf.pageFirst && isPartOf.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageLast.label'})}
                </div>
                <div>
                  {isPartOf.pageFirst && isPartOf.pageLast && `${isPartOf.pageFirst} - ${isPartOf.pageLast}`}
                  {isPartOf.pageFirst && !isPartOf.pageLast && `${isPartOf.pageFirst}`}
                  {!isPartOf.pageFirst && isPartOf.pageLast && `${isPartOf.pageLast}`}
                </div>
              </SubRow>}
              {isPartOf.issue && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.issue.label'})}</div>
                <div>{isPartOf.issue}</div>
              </SubRow>}
              {isPartOf.volume && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.volume.label'})}</div>
                <div>{isPartOf.volume}</div>
              </SubRow>}
              {isPartOf.articleId && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.articleId.label'})}</div>
                <div>{isPartOf.articleId}</div>
              </SubRow>}
            </div>;
          })}
      </Row>);
    }
    if (workDTO.workIsPartOfOthers && workDTO.workIsPartOfOthers.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.isPartOfOther.label'})}>
        {workDTO.workIsPartOfOthers
          .sort((a: WorkIsPartOfOtherDTO, b: WorkIsPartOfOtherDTO): number =>
            ((parseInt(a.volume, 10) || 0) - (parseInt(b.volume, 10) || 0))
            || (parseInt(a.issue, 10) || 0) - (parseInt(b.issue, 10) || 0)
            || (parseInt(a.articleId, 10) || 0) - (parseInt(b.articleId, 10) || 0)
            || (parseInt(a.pageFirst, 10) || 0) - (parseInt(b.pageFirst, 10) || 0))
          .map((isPartOfOther: WorkIsPartOfOtherDTO, index: number): ReactNode => {
            return <div key={index} className={'flex flex-column gap-1'}>
              <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.referencedWork.label'})}</div>
                <div><strong>{isPartOfOther.name}</strong></div>
              </SubRow>
              {(isPartOfOther.pageFirst || isPartOfOther.pageLast) && <SubRow>
                <div>
                  {isPartOfOther.pageFirst && isPartOfOther.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pages.label'})}
                  {isPartOfOther.pageFirst && !isPartOfOther.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageFirst.label'})}
                  {!isPartOfOther.pageFirst && isPartOfOther.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageLast.label'})}
                </div>
                <div>
                  {isPartOfOther.pageFirst && isPartOfOther.pageLast && `${isPartOfOther.pageFirst} - ${isPartOfOther.pageLast}`}
                  {isPartOfOther.pageFirst && !isPartOfOther.pageLast && `${isPartOfOther.pageFirst}`}
                  {!isPartOfOther.pageFirst && isPartOfOther.pageLast && `${isPartOfOther.pageLast}`}
                </div>
              </SubRow>}
              {isPartOfOther.issue && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.issue.label'})}</div>
                <div>{isPartOfOther.issue}</div>
              </SubRow>}
              {isPartOfOther.volume && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.volume.label'})}</div>
                <div>{isPartOfOther.volume}</div>
              </SubRow>}
              {isPartOfOther.articleId && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.articleId.label'})}</div>
                <div>{isPartOfOther.articleId}</div>
              </SubRow>}
            </div>;
          })}
      </Row>);
    }
    if (workDTO.workHasParts && workDTO.workHasParts.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.hasPart.label'})}>
        {workDTO.workHasParts
          .sort((a: WorkHasPartDTO, b: WorkHasPartDTO): number =>
            ((parseInt(a.volume, 10) || 0) - (parseInt(b.volume, 10) || 0))
            || (parseInt(a.issue, 10) || 0) - (parseInt(b.issue, 10) || 0)
            || (parseInt(a.pageFirst, 10) || 0) - (parseInt(b.pageFirst, 10) || 0))
          .map((hasPart: WorkHasPartDTO, index: number): ReactNode => {
            return <div key={index} className={'flex flex-column gap-1'}>
              <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.referencedWork.label'})}</div>
                <div className={'flex flex-row justify-content-between'}>
                  <div><InternalLink to={`/work/${hasPart.referencedWork.id}`}><strong>
                    <div className={'inline-block'}><ParsedMathJax content={hasPart.referencedWork.title} /></div>
                  </strong></InternalLink></div>
                  <div><small className={'white-space-nowrap'}>{`ID: ${hasPart.referencedWork.id}`}</small></div>
                </div>
              </SubRow>
              {(hasPart.pageFirst || hasPart.pageLast) && <SubRow>
                <div>
                  {hasPart.pageFirst && hasPart.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pages.label'})}
                  {hasPart.pageFirst && !hasPart.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageFirst.label'})}
                  {!hasPart.pageFirst && hasPart.pageLast && intl.formatMessage({id: 'work.attr.part.attr.pageLast.label'})}
                </div>
                <div>
                  {hasPart.pageFirst && hasPart.pageLast && `${hasPart.pageFirst} - ${hasPart.pageLast}`}
                  {hasPart.pageFirst && !hasPart.pageLast && `${hasPart.pageFirst}`}
                  {!hasPart.pageFirst && hasPart.pageLast && `${hasPart.pageLast}`}
                </div>
              </SubRow>}
              {hasPart.issue && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.issue.label'})}</div>
                <div>{hasPart.issue}</div>
              </SubRow>}
              {hasPart.volume && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.volume.label'})}</div>
                <div>{hasPart.volume}</div>
              </SubRow>}
              {hasPart.articleId && <SubRow>
                <div>{intl.formatMessage({id: 'work.attr.part.attr.articleId.label'})}</div>
                <div>{hasPart.articleId}</div>
              </SubRow>}
            </div>;
          })}
      </Row>);
    }
    if ((workDTO.internalRelatedIdentifiersSource || []).length || (workDTO.internalRelatedIdentifiersTarget || []).length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.internalRelatedIdentifiers.label'})}>
        {(workDTO.internalRelatedIdentifiersSource || []).map((identifier: WorkRelatedIdentifierInternalDTO, index: number): ReactNode => {
          return <div className={'flex flex-row justify-content-between'} key={`source${index}`}>
            <div>
              {`${intl.formatMessage({id: 'work.attr.internalRelatedIdentifiersSource.attr.thisWork'})} ${getNotation('relation_type_list', identifier.relationType, intl.locale, 'generic')} `}
              <InternalLink to={`/work/${identifier.referencedWork.id}`}>
                <div className={'inline-block'}><ParsedMathJax content={identifier.referencedWork.title} /></div>
              </InternalLink>
            </div>
            <div><small className={'white-space-nowrap'}>{`ID: ${identifier.referencedWork.id}`}</small></div>
          </div>;
        })}
        {(workDTO.internalRelatedIdentifiersTarget || []).map((identifier: WorkRelatedIdentifierInternalDTO, index: number): ReactNode => {
          return <div className={'flex flex-row justify-content-between'} key={`target${index}`}>
            <div className={'flex flex-row gap-2'}>
              <InternalLink to={`/work/${identifier.referencedWork.id}`}>
                <div className={'inline-block'}><ParsedMathJax content={identifier.referencedWork.title} /></div>
              </InternalLink>
              {`${getNotation('relation_type_list', identifier.relationType, intl.locale, 'generic')} ${intl.formatMessage({id: 'work.attr.internalRelatedIdentifiersTarget.attr.thisWork'})} `}
            </div>
            <div><small className={'white-space-nowrap'}>{`ID: ${identifier.referencedWork.id}`}</small></div>
          </div>;
        })}
      </Row>);
    }
    if (workDTO.externalRelatedIdentifiers && workDTO.externalRelatedIdentifiers.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.externalRelatedIdentifiers.label'})}>
        {(workDTO.externalRelatedIdentifiers).map((identifier: WorkRelatedIdentifierDTO, index: number): ReactNode => {
          return <div key={index}>
            {`${intl.formatMessage({id: 'work.attr.externalRelatedIdentifiers.attr.thisWork'})} ${getNotation('relation_type_list', identifier.relationType, intl.locale, 'generic')} ${identifier.relatedIdentifier}`}
          </div>;
        })}
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.specificInfo.title');
  };

  const renderContentData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.workKeywords && workDTO.workKeywords.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.keywords.label' })}>
          {(workDTO.workKeywords || []).map(
            (keyword: WorkKeywordDTO, index: number): ReactNode => (
              <div key={index} data-id={keyword.id}>
                {keyword.keyword}
              </div>
            )
          )}
        </Row>
      );
    }
    if (workDTO.workSubjects && workDTO.workSubjects.length) {
      rows.push(<SubjectsRow subjects={workDTO.workSubjects}/>);
    }
    if (workDTO.workDescriptions && workDTO.workDescriptions.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.descriptions.label'})}>
        {workDTO.workDescriptions.map((description: WorkDescriptionDTO, index: number): ReactNode =>
          <div key={index} className={'flex flex-column gap-4'}>
            <div>{description.content}</div>
            <div className={'flex flex-row'}>
              <div
                className={'w-6'}>{intl.formatMessage({id: 'work.attr.description.attr.language.label'})}: {description.language ? getNotation('language_list', description.language, intl.locale) : ''}</div>
              <div
                className={'w-6'}>{intl.formatMessage({id: 'work.attr.description.attr.type.label'})}: {getNotation('description_types_list', description.descriptionType, intl.locale)}</div>
            </div>
          </div>,
        )}
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.content.title');
  };

  const renderOrganisationsData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.adminDataWork.organisations && workDTO.adminDataWork.organisations.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.belongToOrganisation.label' })}>
          {(workDTO.adminDataWork.organisations || []).map(
            (organisation: ReferencedOrganisationDTO, index: number): ReactNode => (
              <div className={'flex flex-row justify-content-between'}>
                <div>
                  <InternalLink key={index} to={`/organisation/${organisation.id}`}>
                    {organisation.name}
                  </InternalLink>
                </div>
                <div>
                  <small className={'white-space-nowrap'}>{`ID: ${organisation.id}`}</small>
                </div>
              </div>
            )
          )}
        </Row>
      );
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.belongsToOrga.title');
  };

  const renderProjectsData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    if (workDTO.adminDataWork.projects && workDTO.adminDataWork.projects.length) {
      rows.push(
        <Row title={intl.formatMessage({ id: 'work.attr.belongToProject.label' })}>
          {(workDTO.adminDataWork.projects || []).map(
            (project: ReferencedProjectDTO, index: number): ReactNode => (
              <div className={'flex flex-row justify-content-between'}>
                <div>
                  <InternalLink key={index} to={`/project/${project.id}`}>
                    {project.name}
                  </InternalLink>
                </div>
                <div>
                  <small className={'white-space-nowrap'}>{`ID: ${project.id}`}</small>
                </div>
              </div>
            )
          )}
        </Row>
      );
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.belongsToProject.title');
  };

  const renderAdminData = (workDTO: WorkDTO): ReactNode => {
    const rows: ReactNode[] = [];
    rows.push(<Row title={intl.formatMessage({id: 'work.attr.id.label'})}>{workDTO.id}</Row>);
    if (workDTO.adminDataWork.changed) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.changed.label'})}>{localDateTime(workDTO.adminDataWork.changed, intl.locale)}</Row>);
    }
    if (workDTO.adminDataWork.created) {
      workDTO.adminDataWork.created && rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.created.label'})}>{localDateTime(workDTO.adminDataWork.created, intl.locale)}</Row>);
    }
    if (workDTO.adminDataWork.origin) {
      workDTO.adminDataWork.origin && rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.origin.label'})}>{workDTO.adminDataWork.origin}</Row>);
    }
    if (workDTO.adminDataWork.importTenant) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.importTenant.label'})}>{workDTO.adminDataWork.importTenant}</Row>);
    }
    if (workDTO.adminDataWork.deskman) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.deskman.label'})}>{workDTO.adminDataWork.deskman}</Row>);
    }
    if(workDTO.adminDataWork.hidden) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.hidden.label'})}>{boolFilter(workDTO.adminDataWork.hidden || false)}</Row>);
    }
    if (workDTO.adminDataWork.editorialStatus) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.editorialStatus.label'})}>
        {getNotation('editorial_status_list', workDTO.adminDataWork.editorialStatus, intl.locale)}
      </Row>);
    }
    if (workDTO.adminDataWork.adminDataWorkOpenAccess) {
      if (workDTO.adminDataWork.adminDataWorkOpenAccess.adminDataWorkOpenAccessFundeds && workDTO.adminDataWork.adminDataWorkOpenAccess.adminDataWorkOpenAccessFundeds.length) {
        rows.push(<Row
          title={intl.formatMessage({id: 'work.attr.adminDataWorkOpenAccess.attr.adminDataWorkOpenAccessFunded.label'})}>
          {workDTO.adminDataWork.adminDataWorkOpenAccess.adminDataWorkOpenAccessFundeds.map((adminDataWorkOpenAccess: AdminDataWorkOpenAccessFundedDTO, index: number) =>
            <div
              key={index}>{`${intl.formatMessage({id: 'work.attr.adminDataWorkOpenAccess.attr.adminDataWorkOpenAccessFunded.attr.type.label'})} ${getNotation('funded_list', adminDataWorkOpenAccess.type, intl.locale)} - ${intl.formatMessage({id: 'work.admin_data.open_access.funded.affiliation.label'})} ${getNotation('affiliations_list', adminDataWorkOpenAccess.affiliation, intl.locale)}`}</div>,
          )}
        </Row>);
      }
      if (workDTO.adminDataWork.adminDataWorkOpenAccess.accessLevel) {
        rows.push(<Row title={intl.formatMessage({id: 'work.attr.adminDataWorkOpenAccess.attr.accessLevel.label'})}>
          {getNotation('access_level', workDTO.adminDataWork.adminDataWorkOpenAccess.accessLevel, intl.locale)}
        </Row>);
      }
      if (workDTO.adminDataWork.adminDataWorkOpenAccess.articleProcessingCharge) {
        rows.push(<Row
          title={intl.formatMessage({id: 'work.attr.adminDataWorkOpenAccess.attr.articleProcessingCharge.label'})}>
          {workDTO.adminDataWork.adminDataWorkOpenAccess.articleProcessingCharge}
        </Row>);
      }
    }
    if (workDTO.adminDataWork.license) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.license.label'})}>
        {getNotation('license_list', workDTO.adminDataWork.license, intl.locale)}
      </Row>);
    }
    if (workDTO.adminDataWork.licenseText) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.licenseText.label'})}>
        {workDTO.adminDataWork.licenseText}
      </Row>);
    }
    if (workDTO.adminDataWork.licenseManagementStatus) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.licenseManagementStatus.label'})}>
        {getNotation('license_management_status_list', workDTO.adminDataWork.licenseManagementStatus, intl.locale)}
      </Row>);
    }
    if (workDTO.adminDataWork.obligatoryDelivery !== undefined) {
      rows.push(<Row
        title={intl.formatMessage({id: 'work.attr.obligatoryDelivery.label'})}>{boolFilter(workDTO.adminDataWork.obligatoryDelivery)}</Row>);
    }
    if (workDTO.adminDataWork.adminDataWorkOrcidSyncs && workDTO.adminDataWork.adminDataWorkOrcidSyncs.length) {
      rows.push(<Row title={intl.formatMessage({id: 'work.attr.adminDataWorkOrcidSyncs.label'})}>
        <div className={'flex flex-column'}>
          {workDTO.adminDataWork.adminDataWorkOrcidSyncs.map((adminDataWorkOrcidSync: AdminDataWorkOrcidSyncDTO, index: number, array: AdminDataWorkOrcidSyncDTO[]): ReactNode => {
            return <div key={index} className={'flex flex-wrap'}>
              {adminDataWorkOrcidSync.orcidId && <>
                <div className={'w-4'}>{intl.formatMessage({id: 'work.attr.adminDataWorkOrcidSyncs.attr.id.label'})}</div>
                <div className={'w-8'}>{adminDataWorkOrcidSync.orcidId}</div>
              </>}
              {adminDataWorkOrcidSync.orcidPutCode && <>
                <div className={'w-4'}>{intl.formatMessage({id: 'work.attr.adminDataWorkOrcidSyncs.attr.putCode.label'})}</div>
                <div className={'w-8'}>{adminDataWorkOrcidSync.orcidPutCode}</div>
              </>}
              {adminDataWorkOrcidSync.orcidVisibility && <>
                <div className={'w-4'}>{intl.formatMessage({id: 'work.attr.adminDataWorkOrcidSyncs.attr.visibility.label'})}</div>
                <div className={'w-8'}>{adminDataWorkOrcidSync.orcidVisibility}</div>
              </>}
            </div>;
          })}
        </div>
      </Row>);
    }
    return renderDetailAccordionTab(intl, rows, 'work.detail.section.adminData.title');
  };

  return (
    <View
      tags={renderTags(work)}
      title={renderTitle(work)}
      header={
        <>
          <CitationDialog id={work.id} visible={citation} setVisible={setCitation} />
          <DuplicateDialog id={work.id} title={work.title} visible={duplicate} setVisible={setDuplicate} />
          {work.subtitle && renderSubtitle(work)}
          {[...(work.workPersons || []), ...(work.workPersonOthers ?? [])].length > 0 && renderPersons(work)}
          {(work.issued || (work.workIsPartOfs && work.workIsPartOfs.length > 0)) && renderIssuedIsPartOf(work)}
          {((work.workPublishers && work.workPublishers.length > 0) || work.edition) && renderEdition(work)}
          {work.dataVersion && renderDataVersion(work)}
          {work.workDescriptions && work.workDescriptions.length > 0 && renderWorkDescription(work)}
          {work.workKeywords && work.workKeywords.length > 0 && renderKeywords(work)}
          {work.urls && work.urls.length > 0 && renderUrls(work)}
        </>
      }
      toolbar={{
        left: [<Button key={'btn_citation'} icon={<QuoteIcon />} onClick={(): void => setCitation(true)} />].filter(Boolean),
        center: [
          work.adminDataWork.hidden && <Stamp key={'stamp'} red={true}>{intl.formatMessage({ id: 'work.attr.hidden.label' })}</Stamp>,
        ].filter(Boolean),
        right: [
          work.canEdit && (
            <Button
              icon={'pi pi-pencil'}
              onClick={(): void | Promise<void> => navigate(`/work/${work.id}/edit`)}
              title={intl.formatMessage({ id: 'button.edit' })}
            />
          ),
          authorizer.hasAnyOfRoles(['ROLE_EDITOR', 'ROLE_ADMIN']) && (
            <Button
              icon={'pi pi-verified'}
              onClick={(): void => setDuplicate(true)}
              title={intl.formatMessage({ id: 'work.detail.button.setAsOriginal' })}
            />
          ),
          work.canDelete && (
            <Button
              icon={'pi pi-trash'}
              severity={'danger'}
              onClick={(): void => deleteWork(workApi, toast, intl, navigate, work.id, work.title, '/search/works')}
              title={intl.formatMessage({ id: 'button.delete' })}
            />
          ),
          work.canRequestDelete && (
            <Button
              icon={'pi pi-trash'}
              severity={'warning'}
              onClick={(): void =>
                requestDeleteWork(workApi, toast, intl, navigate, work.id, work.title, '/search/works')
              }
              title={intl.formatMessage({ id: 'button.deleteRequest' })}
            />
          ),
        ]
      }}
      details={[
        renderTitleData(work),
        renderBasicData(work),
        renderSpecificData(work),
        renderIsPartOfData(work),
        renderContentData(work),
        renderOrganisationsData(work),
        renderProjectsData(work),
        renderAdminData(work),
      ]}
      tabs={[]}
    />
  );
};

export default DetailWork;
